import { ScrollingModule } from '@angular/cdk/scrolling'
import { CommonModule } from '@angular/common'
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { ColorPickerModule } from 'ngx-color-picker'
import { RichTextEditorModule } from '@syncfusion/ej2-angular-richtexteditor'
import { TreeViewModule } from '@syncfusion/ej2-angular-navigations'
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns'
import { DropDownTreeModule } from '@syncfusion/ej2-angular-dropdowns'
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs'
import { ActiveActionComponent } from './active-action.component'
import { AppLoadingItemComponent } from './app-loading-item.component'
import { AppLogoComponent } from './logo/app-logo.component'
import { BackdropClassDirective } from './services/backdrop-class.directive'
import { ButtonIconComponent } from './button-icon/button-icon.component'
import { ButtonComponent, IconButtonStylerDirective } from './button/button.component'
import { CardComponent } from './card.component'
import { CheckboxSelectButtonComponent } from './checkbox-select-button.component'
import { CheckboxSelectDialogComponent } from './checkbox-select-dialog.component'
import { CheckboxSelectComponent } from './checkbox-select.component'
import { CheckboxTreeComponent } from './checkbox-tree/checkbox-tree.component'
import { ClickElsewhereDirective } from './services/click-elsewhere.directive'
import { ConfirmationDialogComponent } from './confirmation-dialog.component'
import { ConfirmationDialogService } from './services/confirmation-dialog.service'
import { CopyClipboardDirective } from './services/copy-clipboard.directive'
import { CorpLogoComponent } from './corp-logo.component'
import { EntryDialogComponent } from './entry-dialog.component'
import { EntryDialogService } from './services/entry-dialog.service'
import { EditRowDialogComponent } from './edit-row-dialog/edit-row-dialog.component'
import { FieldRadioComponent } from './field-radio.component'
import { FilterInputComponent } from './filter-input.component'
import { FormatCurrency } from './services/format.currency.directive'
import { FormatNumber } from './services/format.onlyNumbers.directive'
import { FormatPercentageDirective } from './services/format.percentage.directive'
import { GeneralHeaderComponent } from './general-header.component'
import { GraphingColorLegendControlsComponent } from './graphing-color-legend-controls.component'
import { GridIconComponent } from './grid-icon.component'
import { HeaderCollapseComponent } from './header-collapse.component'
import { HeaderTitleComponent } from './header-title.component'
import { HierarchicalEntitySelectComponent } from './hierarchical-entity-select.component'
import { HierarchicalEntitySelectService } from './hierarchical-entity-select.service'
import { InfoTextComponent } from './info-text.component'
import { InputComponent } from './input.component'
import { LayerDetailsTitleComponent } from './layer-details-title.component'
import { LayerPropertyComponent } from './layer-property/layer-property.component'
import { LayerService } from './services/layer.service'
import { LossFilterComponent } from './loss-filter-component'
import { MaterialModule } from './material.module'
import { MessageDialogComponent } from './message-dialog-component'
import { MessageDialogService } from './message-dialog.service'
import { MetricTextComponent } from './metric-text.component'
import { MetricsTogglesComponent } from './metrics-toggles.component'
import { NamedNgTemplateDirective } from './services/named-ng-template.directive'
import { NavListItemComponent } from './nav-list-item.component'
import { OverviewCardComponent } from './overview-card.component'
import { PanelToggleButtonComponent } from './panel-hide-button.component'
import { PercentageInputComponent } from './percentage-input/percentage-input.component'
import { PipesModule } from './pipes/pipes.module'
import { PlaceholderImageComponent } from './placeholder-image.component'
import { RankComponent } from './rank.component'
import { ReturnPeriodSelectorComponent } from './return-period-selector.component'
import { ReturnPeriodTogglesComponent } from './return-period-toggles.component'
import { SetNameDialogComponent } from './set-name-dialog/set-name-dialog.component'
import { ImportLossSetsDialogComponent } from 'src/app/shared/import-loss-sets-dialog.component'
import { ShortcutTileComponent } from './shortcut-tile.component'
import { SliderInputComponent } from './slider-input'
import { SortTableFilterComponent } from './sort-table/sort-table-filter/sort-table-filter.component'
import { SortTableValueComponent } from './sort-table/sort-table-value/sort-table-value.component'
import { SortTableComponent } from './sort-table/sort-table.component/sort-table.component'
import { SvgDefsComponent } from './svg-defs.component'
import { TableDialogComponent } from './table-dialog/table-dialog.component'
import { TableDialogService } from './table-dialog/table-dialog.service'
import { TagComponent } from './tag.component'
import { ThemeComponent } from './theme.component'
import { ThemeContainerComponent } from './theme.container'
import { TriggerPositionDialogComponent } from './trigger-position-dialog.component'
import { UserCardComponent } from './user-card.component'
import { WeightSliderComponent } from './weight-slider.component'
import { WheelScrollDirective } from './services/wheel-scroll.directive'
import { MISummaryValueComponent } from './mi-summary-value.component'
import { MultiselectAutocompleteComponent } from './multiselect-autocomplete.component'
import { MultiselectLayerPropertyComponent } from './multiselect-layer-property/multiselect-layer-property.component'
import { MultiselectLayerPropertyDialogComponent } from './multiselect-layer-property/dialog/multiselect-layer-property-dialog.component'
import { DependentLayerPropertyComponent } from './dependent-layer-property/dependent-layer-property.component'
import { DependentLayerPropertyDialogComponent } from './dependent-layer-property/dialog/dependent-layer-property-dialog.component'
import { PricingCurveSavedCurveTooltipComponent } from './saved-pricing-curve-tooltip/pricing-curve-saved-curve-tooltip.component'
import { PricingCurveSavedCurvesComponent } from './add-saved-curve-dialog/pricing-curve-saved-curves.component'
import { PricingCurveSavedCurveFilterBarComponent } from './add-saved-curve-dialog/saved-curve-filter-bar/pricing-curve-saved-curve-filter-bar.component'
import { MultiselectFilterComponent } from './add-saved-curve-dialog/multiselect-filter-component/multiselect-filter.component'
import { NewGroupDialogComponent } from './new-group-dialog/new-group-dialog.component'
import { RenameGroupDialogComponent } from './rename-group-dialog.component'
import { SkeletonComponent } from './skeleton/skeleton.component'
import { ScenarioSelectorComponent } from './scenario-selector/scenario-selector.component'
import { TestingExcelComponent } from './sort-table-tester/testing-excel.component'
import { PivotTableComponent } from './pivot-table/pivot-table-component/pivot-table.component'
import { HoverClassDirective } from './services/hover-class.directive'
import { ContactSupportButtonDialogComponent } from './contact-support/contact-support-button-support-dialog/contact-support-button-dialog.component'
import { ContactSupportIconComponent } from './contact-support/contact-support-icon/contact-support-icon.component'
import { ContactSupportIconContainerComponent } from './contact-support/contact-support-icon-container/contact-support-icon-container.component'
import { DropdownTreeComponent } from './dropdown-tree/dropdown-tree.component'

const imports = [
  ColorPickerModule,
  CommonModule,
  FormsModule,
  MaterialModule,
  PipesModule,
  RouterModule,
  ScrollingModule,
  RichTextEditorModule,
  TreeViewModule,
  DropDownListModule,
  DropDownTreeModule,
  TextBoxModule,
]

const COMPONENTS = [
  ContactSupportIconContainerComponent,
  CheckboxSelectDialogComponent,
  ConfirmationDialogComponent,
  EntryDialogComponent,
  ImportLossSetsDialogComponent,
  MessageDialogComponent,
  SetNameDialogComponent,
  SortTableFilterComponent,
  SortTableValueComponent,
  TableDialogComponent,
  TestingExcelComponent,
]

const EXPORTS = [
  ActiveActionComponent,
  AppLoadingItemComponent,
  AppLogoComponent,
  BackdropClassDirective,
  ButtonComponent,
  ButtonIconComponent,
  CardComponent,
  CheckboxSelectButtonComponent,
  CheckboxSelectComponent,
  ClickElsewhereDirective,
  CopyClipboardDirective,
  CorpLogoComponent,
  EditRowDialogComponent,
  FieldRadioComponent,
  FilterInputComponent,
  FormatCurrency,
  FormatNumber,
  FormatPercentageDirective,
  GeneralHeaderComponent,
  GraphingColorLegendControlsComponent,
  GridIconComponent,
  HeaderCollapseComponent,
  HeaderTitleComponent,
  HierarchicalEntitySelectComponent,
  IconButtonStylerDirective,
  InfoTextComponent,
  InputComponent,
  LayerDetailsTitleComponent,
  LayerPropertyComponent,
  MultiselectLayerPropertyComponent,
  DependentLayerPropertyComponent,
  DependentLayerPropertyDialogComponent,
  MetricTextComponent,
  MetricsTogglesComponent,
  NamedNgTemplateDirective,
  NavListItemComponent,
  OverviewCardComponent,
  PanelToggleButtonComponent,
  PercentageInputComponent,
  PlaceholderImageComponent,
  RankComponent,
  ReturnPeriodSelectorComponent,
  ReturnPeriodTogglesComponent,
  ShortcutTileComponent,
  SliderInputComponent,
  SortTableComponent,
  SvgDefsComponent,
  TagComponent,
  ThemeComponent,
  ThemeContainerComponent,
  TriggerPositionDialogComponent,
  UserCardComponent,
  WeightSliderComponent,
  WheelScrollDirective,
  HoverClassDirective,
  LossFilterComponent,
  MISummaryValueComponent,
  MultiselectAutocompleteComponent,
  MultiselectLayerPropertyDialogComponent,
  NewGroupDialogComponent,
  RenameGroupDialogComponent,
  SkeletonComponent,
  PricingCurveSavedCurveTooltipComponent,
  PricingCurveSavedCurvesComponent,
  PricingCurveSavedCurveFilterBarComponent,
  MultiselectFilterComponent,
  ScenarioSelectorComponent,
  PivotTableComponent,
  TestingExcelComponent,
  ContactSupportButtonDialogComponent,
  ContactSupportIconComponent,
  ContactSupportIconContainerComponent,
  CheckboxTreeComponent,
  DropdownTreeComponent,
]

const providers = [
  ConfirmationDialogService,
  EntryDialogService,
  HierarchicalEntitySelectService,
  LayerService,
  MessageDialogService,
  TableDialogService,
]

@NgModule({
  declarations: [...COMPONENTS, ...EXPORTS],
  exports: [...imports, ...EXPORTS],
  imports,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers,
})
export class SharedModule {}
